import { create } from 'zustand';
import { WorkerApi } from '@shared/types/api';
import { isElectron } from '@shared/utils/is-electron';
import { IpcChannel } from '@shared/types/ipc';
import { useApiServiceStore } from './api';
import { useWalletEnvStore } from './wallet-env';
import { useRemoteResourcesStore } from './remote-resources';
import { SortBy } from '@ewf/pages/Discover/WorkerList/SortBy';
import { FilterBy } from '@ewf/pages/Discover/WorkerList/FilterBy';
import { useExperimentalMode } from './experimental-mode';

const STORAGE_KEYS = {
  SORT_BY: 'solution-groups-sort-by',
  FILTER_BY: 'solution-groups-filter-by',
} as const;

type SolutionGroupsStore = {
  init: () => void;
  cleanUp: () => void;
  isInitialized: boolean;
  isLoading: boolean;
  errorMsg: string;
  solutionGroups: WorkerApi.GetSolutionGroups;
  whitelistedSolutionGroupIds: string[];
  sortBy: SortBy;
  filterBy: FilterBy;
  setSortBy: (sortBy: SortBy) => void;
  setFilterBy: (filterBy: FilterBy) => void;
  fetchSolutionGroups: (options?: { showLoading: boolean }) => Promise<void>;
  setFavourite: (solutionGroupId: string, isFavourite: boolean) => Promise<void>;
};

const getStoredSortBy = (): SortBy => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SORT_BY);
    return stored ? JSON.parse(stored) : { criteria: 'name', order: 'ascending' };
  } catch {
    return { criteria: 'name', order: 'ascending' };
  }
};

const getStoredFilterBy = (): FilterBy => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.FILTER_BY);
    return stored ? JSON.parse(stored) : { unAuditedPools: false, expiredPools: true };
  } catch {
    return { unAuditedPools: false, expiredPools: true };
  }
};

export const useSolutionGroupsStore = create<SolutionGroupsStore>((set, get) => ({
  init: () => {
    if (get().isInitialized) return;

    const storedSortBy = getStoredSortBy();
    const storedFilterBy = getStoredFilterBy();

    set({
      isInitialized: true,
      sortBy: storedSortBy,
      filterBy: storedFilterBy,
    });

    get().fetchSolutionGroups({ showLoading: true });

    if (isElectron()) {
      window.Api.on(IpcChannel.updateFetchWorkers, () => {
        get().fetchSolutionGroups();
      });

      // Clean all listeners when component unmounts
      return () => {
        window.Api.removeAllListeners(IpcChannel.updateFetchWorkers);
      };
    }

    useWalletEnvStore.subscribe((state, previousState) => {
      if (state.env !== previousState.env) {
        useSolutionGroupsStore.getState().fetchSolutionGroups({ showLoading: true });
      }
    });

    useExperimentalMode.subscribe((state, previousState) => {
      if (state.isExperimental !== previousState.isExperimental && state.isExperimental) {
        useSolutionGroupsStore.getState().setFilterBy({
          unAuditedPools: true,
          expiredPools: true,
        });
      }
    });
  },
  cleanUp: () => {
    set({ isInitialized: false, solutionGroups: [], whitelistedSolutionGroupIds: [] });
  },
  isInitialized: false,
  isLoading: false,
  errorMsg: '',
  solutionGroups: [],
  whitelistedSolutionGroupIds: [],
  sortBy: getStoredSortBy(),
  filterBy: getStoredFilterBy(),
  setSortBy: (sortBy: SortBy) => {
    localStorage.setItem(STORAGE_KEYS.SORT_BY, JSON.stringify(sortBy));
    set({ sortBy });
  },
  setFilterBy: (filterBy: FilterBy) => {
    localStorage.setItem(STORAGE_KEYS.FILTER_BY, JSON.stringify(filterBy));
    set({ filterBy });
  },
  fetchSolutionGroups: async (options?: { showLoading: boolean }) => {
    const showLoading = options?.showLoading;

    try {
      if (showLoading) {
        set({ isLoading: true });
      }

      const apiService = useApiServiceStore.getState().api;
      const solutionGroups = await apiService.getSolutionGroups();
      const whitelistedSolutionGroupIds =
        useRemoteResourcesStore.getState().whitelistedSolutionGroupIds;

      set({
        solutionGroups: solutionGroups || [],
        whitelistedSolutionGroupIds: whitelistedSolutionGroupIds || [],
      });
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'Failed to fetch solution groups';
      set({ errorMsg });
    } finally {
      if (showLoading) {
        set({ isLoading: false });
      }
    }
  },
  setFavourite: async (solutionGroupId, isFavourite) => {
    const updatedSolutionGroups = get().solutionGroups.map((sg) => {
      if (sg.id !== solutionGroupId) return sg;
      return { ...sg, isFavourites: isFavourite };
    });

    set({ solutionGroups: updatedSolutionGroups });
  },
}));
